import { api } from '@/shared/api/client'

/**
 * La única puerta de red del portal.
 *
 * Dos cosas quedan cerradas acá y no en la disciplina de quien escriba la
 * próxima pantalla:
 *
 * 1. **Solo GET.** No se exporta nada más. Para escribir habría que importar
 *    `api` de nuevo, y `api.ts` del portal no lo hace: el test
 *    `portal-read-only.test.ts` verifica que este archivo sea el ÚNICO de
 *    `features/portal/` que importa el cliente HTTP.
 * 2. **Siempre bajo `portal/`.** Un path del portal no puede apuntar por
 *    accidente a un endpoint interno: el prefijo lo pone esta función, no el
 *    que llama.
 *
 * Del otro lado el backend sostiene lo mismo por su cuenta
 * (`app/api/router_portal.py` explota al arrancar si alguien cuelga una
 * mutación del portal). Son dos capas independientes, a propósito.
 */

/**
 * "Ver como cliente": el admin mira el portal de una empresa antes de una
 * reunión.
 *
 * El backend acepta `?as_company=<uuid>` en **todos** los GET del portal y lo
 * autoriza él: solo un interno con acceso a esa empresa, y sirviendo los mismos
 * `schemas_client.py` de siempre. El frontend no simula nada ni filtra de su
 * lado —eso sería inventar un portal que no es el que ve el cliente—: pasa el
 * parámetro y muestra lo que el servidor conteste.
 *
 * Para un `client` el parámetro no se manda nunca: su empresa sale de su fila
 * en `users`, no de la URL.
 */
export function portalGet<T>(path: string, asCompany?: string | null): Promise<T> {
  const clean = path.replace(/^\/+/, '')
  if (!asCompany) return api.get<T>(`portal/${clean}`)

  const separator = clean.includes('?') ? '&' : '?'
  return api.get<T>(`portal/${clean}${separator}as_company=${encodeURIComponent(asCompany)}`)
}
